<script setup>
import { ref, computed, onMounted, watch, toRef, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import dayjs from 'dayjs'
import { useQuasar } from 'quasar'

import { planningApi } from '@/apis/planning.api'
import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useScheduleStore } from '@/stores/schedule.store'
import { rangeForPreset, savedPreset } from '@/composables/date-range-presets'
import { useMiddleButtonPan } from '@/composables/scroll'
import { sortByFields } from '@/composables/filter-sort'

import FilterSortSelector from '@/components/shared/FilterSortSelector.vue'
import WarehouseFilterSelect from '@/components/shared/WarehouseFilterSelect.vue'
import DateRangeNavigator from '@/components/shared/DateRangeNavigator.vue'
import EquipmentDetail from '@/components/shared/EquipmentDetail.vue'
import StaffDetail from '@/components/shared/StaffDetail.vue'
import StaffMovementsEdit from '@/components/shared/StaffMovementsEdit.vue'
import WarehouseMovementEdit from '@/components/shared/WarehouseMovementEdit.vue'

const $q = useQuasar()
const route = useRoute()
const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const scheduleStore = useScheduleStore()
const { serviceTypeEnum } = portalStore
const { availabilitySortFieldEnum } = webSiteStore

const defaultPeriod = webSiteStore.availabilityMatrixPeriod
  ?? webSiteStore.getDefaultTimelinePeriod()
  ?? portalStore.dateRangePresetEnum.MONTH
const initialRange = rangeForPreset(defaultPeriod) ?? rangeForPreset(portalStore.dateRangePresetEnum.MONTH)
const dateRange = ref({
  from: initialRange.from.format('YYYY-MM-DD'),
  to: initialRange.to.format('YYYY-MM-DD'),
})
const selectedWarehouseId = ref(null)
const resourceType = ref(serviceTypeEnum.EQUIPMENT)
const loading = ref(false)
const addAllocation = ref(null)
const addMovement = ref(null)
const selectedItem = ref(null)
const namesCollapsed = toRef(webSiteStore, 'availabilityMatrixNamesCollapsed')

const warehouses = ref([])
const availability = ref({})
const assignments = ref([])
const loadedRange = ref(null)
const sortedIds = ref([])
const matrixScroll = ref(null)
const { onMouseDown: onMatrixMouseDown } = useMiddleButtonPan(matrixScroll)

const scrollToToday = () => nextTick(() => {
  const container = matrixScroll.value
  const todayCol = container?.querySelector('.day-col--today')
  if (!todayCol) return
  const nameColWidth = container.querySelector('.corner')?.offsetWidth ?? 0
  container.scrollLeft = todayCol.offsetLeft - nameColWidth
})

const visibleDays = computed(() => {
  const start = dayjs(dateRange.value.from).startOf('day')
  const count = dayjs(dateRange.value.to).startOf('day').diff(start, 'day') + 1
  return Array.from({ length: Math.max(count, 0) }, (_, i) => start.add(i, 'day'))
})

const resourceAvatarIcon = computed(() =>
  resourceType.value === serviceTypeEnum.EQUIPMENT ? 'mdi-camera-outline' : 'mdi-account'
)

const isStaffResource = computed(() => resourceType.value === serviceTypeEnum.STAFF)
const selectedWarehouse = computed(() => warehouses.value.find(w => w.id === selectedWarehouseId.value) ?? null)

const selectableServices = (services) =>
  (services ?? []).filter(s => !s.archived && s.type !== serviceTypeEnum.GROUP)

const baseServices = computed(() => selectableServices(
  resourceType.value === serviceTypeEnum.EQUIPMENT ? planningStore.equipments : planningStore.staff
))

const locationFilteredServices = computed(() => {
  if (selectedWarehouseId.value === null) return baseServices.value
  return baseServices.value.filter(s => `${selectedWarehouseId.value}_${s.id}` in availability.value)
})

const filterSort = computed(() => webSiteStore.filterSortFields[route.name])

const services = computed(() => {
  let items = [...locationFilteredServices.value]
  const search = filterSort.value?.filterValue
  if (search) {
    const q = search.toLowerCase()
    items = items.filter(s => planningStore.getServiceName(s).toLowerCase().includes(q))
  }
  const positions = new Map(sortedIds.value.map((id, index) => [id, index]))
  return items.sort((a, b) => (positions.get(a.id) ?? 0) - (positions.get(b.id) ?? 0))
})

const matrixLoading = computed(() => loading.value || !(
  loadedRange.value &&
  loadedRange.value.resource === resourceType.value &&
  loadedRange.value.from === dateRange.value.from &&
  loadedRange.value.to === dateRange.value.to
))

const sortValue = (service, field) => {
  switch (field) {
    case availabilitySortFieldEnum.AVAILABILITY: return getQty(service.id, dayjs(dateRange.value.from))
    case availabilitySortFieldEnum.CONDITION: return service.condition_value ?? null
    case availabilitySortFieldEnum.FIRST_NAME: return service.first_name?.toLowerCase() || null
    default: return service.name?.toLowerCase() || null
  }
}

const applySort = () => {
  sortedIds.value = sortByFields(baseServices.value, filterSort.value?.sortFields, sortValue).map(s => s.id)
}

const tabBg = computed(() => $q.dark.isActive ? 'bg-grey-9' : 'bg-white')

const getQty = (serviceId, day) => {
  const dayStart = day.startOf('day')
  const dayEnd = day.endOf('day')
  let total = 0
  for (const [key, intervals] of Object.entries(availability.value)) {
    const [wIdStr, sIdStr] = key.split('_')
    if (parseInt(sIdStr) !== serviceId) continue
    if (selectedWarehouseId.value !== null && parseInt(wIdStr) !== selectedWarehouseId.value) continue
    for (const interval of intervals) {
      if (dayjs(interval.from).isBefore(dayEnd) && dayjs(interval.to).isAfter(dayStart)) {
        total += interval.quantity
        break
      }
    }
  }
  return total
}

const qtyTier = (service, qty) => {
  if (qty <= 0) return 'none'
  const singleCountsAsFull = service.is_serialized
    || service.type === serviceTypeEnum.STAFF
    || Number(service.total_quantity ?? 0) <= 1
  return (qty > 1 || singleCountsAsFull) ? 'ok' : 'low'
}

const projectAssignments = computed(() => assignments.value.filter(event =>
  event.project_id > 0 && event.service_id && planningStore.getProjectById(event.project_id)?.color
))

const eachAssignmentDay = (event, callback) => {
  let day = dayjs(event.start).startOf('day')
  const end = dayjs(event.end).startOf('day')
  while (day.diff(end, 'day') <= 0) {
    callback(day.format('YYYY/MM/DD'))
    day = day.add(1, 'day')
  }
}

const serviceProjectLanes = computed(() => {
  const firstStart = {}
  for (const event of projectAssignments.value) {
    const key = `${event.service_id}_${event.project_id}`
    const start = dayjs(event.start).valueOf()
    if (firstStart[key] === undefined || start < firstStart[key]) firstStart[key] = start
  }
  const byService = {}
  for (const [key, start] of Object.entries(firstStart)) {
    const [serviceId, projectId] = key.split('_').map(Number)
    ;(byService[serviceId] ??= []).push({ projectId, start })
  }
  for (const serviceId of Object.keys(byService)) {
    byService[serviceId] = byService[serviceId]
      .sort((a, b) => a.start - b.start || a.projectId - b.projectId)
      .map(entry => entry.projectId)
  }
  return byService
})

const activeProjectsByServiceDay = computed(() => {
  const map = {}
  for (const event of projectAssignments.value) {
    eachAssignmentDay(event, dayKey => {
      (map[`${event.service_id}_${dayKey}`] ??= new Set()).add(event.project_id)
    })
  }
  return map
})

const assignmentLanes = (serviceId, day) => {
  const lanes = serviceProjectLanes.value[serviceId]
  if (!lanes?.length) return []
  const active = activeProjectsByServiceDay.value[`${serviceId}_${day.format('YYYY/MM/DD')}`]
  return lanes.map(projectId => active?.has(projectId)
    ? planningStore.getProjectById(projectId)?.color || null
    : null)
}

const toArray = (v) => {
  if (Array.isArray(v)) return v
  if (v?.results) return v.results
  if (v?.value) return v.value
  if (v?.data?.results) return v.data.results
  if (v?.data?.value) return v.data.value
  if (Array.isArray(v?.data)) return v.data
  return []
}

const fetchAvailabilityAsync = async () => {
  const serviceIds = baseServices.value.map(s => s.id)
  const resource = resourceType.value
  const { from, to } = dateRange.value
  if (!serviceIds.length) { availability.value = {}; assignments.value = []; loadedRange.value = null; return }
  const dateFrom = dayjs(from).startOf('day').toISOString()
  const dateTo = dayjs(to).endOf('day').toISOString()
  loading.value = true
  try {
    const [availRes, eventsRes] = await Promise.all([
      planningApi.getStockAvailabilityAsync({
        service_ids: serviceIds.join(','),
        date_from: dateFrom,
        date_to: dateTo,
        include_scheduled: true,
        include_virtual: true,
      }),
      scheduleStore.fetchEventsByServiceIdsAsync(serviceIds, { start: dateFrom, end: dateTo }),
    ])
    availability.value = availRes?.data ?? {}
    assignments.value = toArray(eventsRes)
    loadedRange.value = { from, to, resource }
    scrollToToday()
  } finally {
    loading.value = false
  }
}

const loadDataAsync = async () => {
  loading.value = true
  try {
    const [, whRes] = await Promise.all([
      planningStore.fetchEquipmentsAsync({ all: true }, true),
      planningStore.fetchWarehousesAsync({ all: true }),
      planningStore.groups.length === 0 ? planningStore.fetchGroupsAsync({ all: true }, true) : Promise.resolve(),
      planningStore.projects?.length ? Promise.resolve() : planningStore.fetchProjectsAsync({ all: true }, true),
    ])

    warehouses.value = toArray(whRes).filter(w => !w.archived)

    if (selectedWarehouseId.value === null) {
      const defWh = webSiteStore.getDefaultWarehouse()
      selectedWarehouseId.value = (defWh && warehouses.value.find(w => w.id === defWh))
        ? defWh
        : (warehouses.value[0]?.id ?? null)
    }

    await fetchAvailabilityAsync()
    applySort()
  } finally {
    loading.value = false
  }
}

const onRangeChange = (range) => {
  dateRange.value = range
  fetchAvailabilityAsync()
}

const onResourceTypeChange = async (resource) => {
  resourceType.value = resource
  webSiteStore.setAvailabilitySortFields(resource)
  loading.value = true
  try {
    if (resource === serviceTypeEnum.STAFF) {
      await planningStore.fetchStaffsAsync({ all: true }, true)
    }
    await fetchAvailabilityAsync()
    applySort()
  } finally {
    loading.value = false
  }
}

const onLocationSelect = (id) => {
  selectedWarehouseId.value = id
  applySort()
}

const toggleNames = () => {
  namesCollapsed.value = !namesCollapsed.value
}

const onCellTap = (service, day) => {
  const startDate = day.format('YYYY-MM-DD')
  if (isStaffResource.value) {
    addAllocation.value = { service: service.id, startDate }
  } else {
    addMovement.value = { service, startDate }
  }
}

const onAllocationAdded = async () => {
  addAllocation.value = null
  await fetchAvailabilityAsync()
  applySort()
}

const onMovementAdded = async () => {
  addMovement.value = null
  await fetchAvailabilityAsync()
  applySort()
}

const onDetailCancel = () => { selectedItem.value = null }

const onDetailUpdate = async () => {
  selectedItem.value = null
  await (resourceType.value === serviceTypeEnum.STAFF
    ? planningStore.fetchStaffsAsync({ all: true }, true)
    : planningStore.fetchEquipmentsAsync({ all: true }, true))
  await fetchAvailabilityAsync()
  applySort()
}

watch(() => filterSort.value?.sortFields, applySort, { deep: true })

onMounted(() => {
  webSiteStore.fabActions = []
  webSiteStore.setAvailabilitySortFields(resourceType.value)
  loadDataAsync()
})
</script>

<template>
  <div class="availability-mobile availability-mobile--fullscreen" :class="tabBg">
    <div class="date-header" :class="tabBg">
      <filter-sort-selector hide-archived />

      <q-separator />

      <div class="date-nav-row">
        <warehouse-filter-select
          class="location-item"
          allow-all
          :model-value="selectedWarehouseId"
          :warehouses="warehouses"
          @update:model-value="onLocationSelect"
        />

        <div class="period-nav">
          <date-range-navigator
            :model-value="dateRange"
            :default-preset="defaultPeriod"
            compact
            @update:model-value="onRangeChange"
            @preset="webSiteStore.availabilityMatrixPeriod = savedPreset($event)"
          />
        </div>
      </div>
      <q-separator />
    </div>

    <div ref="matrixScroll" class="matrix-scroll" @mousedown="onMatrixMouseDown">
      <table class="matrix" :class="{ 'matrix--collapsed': namesCollapsed }">
        <thead>
          <tr>
            <th class="corner" :class="tabBg">
              <q-btn
                flat
                dense
                round
                size="sm"
                color="grey-7"
                :icon="namesCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-left'"
                @click="toggleNames"
              >
                <q-tooltip>{{ namesCollapsed ? $t('common.expand') : $t('common.collapse') }}</q-tooltip>
              </q-btn>
            </th>
            <th
              v-for="day in visibleDays"
              :key="day.format('YYYY-MM-DD')"
              class="day-col"
              :class="[tabBg, { 'day-col--today': day.isSame(dayjs(), 'day') }]"
            >
              <div class="text-body2 text-weight-bold">{{ day.format('D') }}</div>
              <div class="day-col__weekday">{{ day.format('ddd') }}</div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="service in services" :key="service.id">
            <td class="name-col name-col--clickable" :class="tabBg" @click="selectedItem = service">
              <div class="name-col__inner">
                <q-avatar
                  class="name-col__avatar"
                  size="sm"
                  color="grey-4"
                  text-color="white"
                >
                  <q-img
                    v-if="service.avatar_file || service.avatar_url"
                    :src="service.avatar_file || service.avatar_url"
                  />
                  <q-icon v-else :name="resourceAvatarIcon" size="xs" />
                  <q-tooltip v-if="namesCollapsed">{{ planningStore.getServiceName(service) }}</q-tooltip>
                </q-avatar>
                <span v-if="!namesCollapsed" class="name-col__label ellipsis">
                  {{ planningStore.getServiceName(service) }}
                </span>
              </div>
            </td>
            <td
              v-for="day in visibleDays"
              :key="day.format('YYYY-MM-DD')"
              class="qty-cell qty-cell--clickable"
              :class="{ 'qty-cell--today': day.isSame(dayjs(), 'day') }"
              @click="onCellTap(service, day)"
            >
              <div class="qty-slot">
                <div class="qty-bar" :class="`qty-bar--${qtyTier(service, getQty(service.id, day))}`">
                  {{ getQty(service.id, day) }}
                </div>
                <div
                  v-for="(color, i) in assignmentLanes(service.id, day)"
                  :key="i"
                  class="assign-strip"
                  :style="{ backgroundColor: color || 'transparent' }"
                />
              </div>
            </td>
          </tr>
          <tr v-if="!matrixLoading && services.length === 0">
            <td :colspan="visibleDays.length + 1" class="text-center text-grey-5 q-pa-lg">
              {{ $t('common.noData') }}
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="matrixLoading" class="matrix-loading">
        <q-spinner color="grey-5" size="lg" />
      </div>
    </div>

    <q-tabs
      :model-value="resourceType"
      align="justify"
      active-color="primary"
      indicator-color="primary"
      :class="['text-grey', 'bottom-tabs', tabBg]"
      @update:model-value="onResourceTypeChange"
    >
      <q-tab :name="serviceTypeEnum.EQUIPMENT" :label="$t('common.equipment', 2)" />
      <q-tab :name="serviceTypeEnum.STAFF" :label="$t('common.staff', 2)" />
    </q-tabs>

    <q-dialog maximized :model-value="selectedItem !== null" @hide="selectedItem = null">
      <staff-detail
        v-if="selectedItem && resourceType === serviceTypeEnum.STAFF"
        :model-value="selectedItem"
        @cancel="onDetailCancel"
        @update="onDetailUpdate"
      />
      <equipment-detail
        v-else-if="selectedItem"
        :model-value="selectedItem"
        @cancel="onDetailCancel"
        @update="onDetailUpdate"
      />
    </q-dialog>

    <q-dialog maximized :model-value="addAllocation !== null" @hide="addAllocation = null">
      <staff-movements-edit
        v-if="addAllocation !== null"
        :service="addAllocation.service"
        :start-date="addAllocation.startDate"
        @update="onAllocationAdded"
        @cancel="addAllocation = null"
      />
    </q-dialog>

    <q-dialog maximized :model-value="addMovement !== null" @hide="addMovement = null">
      <warehouse-movement-edit
        v-if="addMovement !== null"
        :model-value="{}"
        :service="addMovement.service"
        :warehouse="selectedWarehouse"
        :scheduled-date="addMovement.startDate"
        @update="onMovementAdded"
        @cancel="addMovement = null"
        @delete="onMovementAdded"
      />
    </q-dialog>

  </div>
</template>

<style scoped lang="scss">
.availability-mobile {
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &--fullscreen {
    position: absolute;
    inset: 0;
  }

}

.date-header {
  flex-shrink: 0;
  z-index: 10;
}

.location-item {
  flex: 0 1 auto;
  min-width: 0;
  min-height: 2.25rem;
  padding: .25rem .375rem;
}

.date-nav-row {
  display: flex;
  align-items: center;
  padding: .125rem .25rem;
  gap: .125rem;
}

.period-nav {
  flex: 0 1 auto;
  min-width: 0;
  margin-left: auto;
  overflow-x: auto;

  :deep(.q-select) { min-width: 7rem !important; }
  :deep(.q-btn) { padding: 0; min-width: 1.75rem; }
}

.matrix-scroll {
  flex: 1 1 auto;
  overflow: auto;
  position: relative;

  &.cursor-grabbing {
    cursor: grabbing;
    user-select: none;
  }
}


.matrix {
  border-collapse: separate;
  border-spacing: 0;
  width: auto;

  th, td {
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }

  .corner {
    position: sticky;
    top: 0;
    left: 0;
    z-index: 3;
    width: 10rem;
    text-align: left;
    padding: .25rem;
  }

  .day-col {
    position: sticky;
    top: 0;
    z-index: 2;
    width: 3rem;
    min-width: 3rem;
    padding: .25rem .125rem;
    text-align: center;
    user-select: none;

    &__weekday {
      font-size: 0.65rem;
      line-height: 1;
      opacity: 0.7;
    }

    &--today {
      color: $primary;
      font-weight: bold;
      box-shadow: inset 0 0 0 100px rgba($primary, 0.12);
    }
  }

  .name-col {
    position: sticky;
    left: 0;
    z-index: 1;
    width: 10rem;
    max-width: 10rem;
    height: 2.5rem;
    vertical-align: middle;
    padding: 0 .5rem;

    &__inner {
      display: flex;
      align-items: center;
      gap: .5rem;
    }

    &__label {
      min-width: 0;
      font-size: .8125rem;
    }

    &--clickable {
      cursor: pointer;
    }
  }

  &--collapsed {
    .corner, .name-col {
      width: 2.75rem;
      min-width: 2.75rem;
    }
    .name-col { padding: 0 .25rem; }
    .name-col__inner { justify-content: center; }
  }

  .qty-cell {
    width: 3rem;
    min-width: 3rem;
    height: 2.5rem;
    padding: .75rem 0 0;
    vertical-align: top;

    &--today { background: rgba($primary, 0.07); }

    &--clickable {
      cursor: pointer;
      &:active { background: rgba($primary, 0.16); }
    }
  }

  .qty-slot {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
    width: 100%;
  }

  .qty-bar {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 1rem;
    width: 100%;
    margin-bottom: .5rem;
    font-size: .7rem;
    font-weight: bold;
    color: #fff;

    &--ok { background: rgba(67, 160, 71, 0.9); }
    &--low { background: rgba(251, 140, 0, 0.92); }
    &--none { background: rgba(229, 57, 53, 0.6); }
  }

  .assign-strip {
    height: 3px;
    width: 100%;
  }
}

.body--dark .matrix {
  th, td { border-bottom-color: rgba(255, 255, 255, 0.10); }
}

.matrix-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.4);
}

.body--dark .matrix-loading {
  background: rgba(0, 0, 0, 0.4);
}

.bottom-tabs {
  flex-shrink: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
}
</style>
